import * as React from "react";
import { uniqBy, uniq } from "ramda";
import useTable from "../../useTable";
import { Config } from "../../context";
import { getLabel, getColumnValue } from "../../util";

import StringBase from "./String";
import EnumBase from "./Enum";
import BoolBase from "./Bool";
import RangeBase from "./Range";
import DateBase from "./Date";
import TagsBase from "./Tags";

type Props = {
  config: Config;
};

/**
 * Helpers
 */

function useFilter(config: Config, externalRef: any) {
  const { getFilter, setFilterValue, data, t } = useTable();

  const componentRef = React.useRef<{ focus?: () => void }>(null);

  const key = config.key as string;
  const filter = getFilter(key);

  React.useImperativeHandle(externalRef, () => ({
    focus: () => {
      if (componentRef.current && componentRef.current!.focus) {
        componentRef.current.focus();
      }
    }
  }));

  return {
    value: filter.value,
    onChange: (value: any) => setFilterValue(key, value),
    componentRef,
    data,
    t
  };
}

function usePlaceholder(config: Config) {
  const { t } = useTable();

  const placeholder =
    typeof config.filterPlaceholder === "function"
      ? config.filterPlaceholder(t)
      : config.filterPlaceholder;

  return placeholder;
}

/**
 * Components
 */

// tslint:disable-next-line: variable-name
const String = React.forwardRef(function String({ config }: Props, ref: any) {
  const { componentRef, onChange, value, data, t } = useFilter(config, ref);
  const placeholder = usePlaceholder(config);

  return (
    <StringBase
      items={uniq(data.map(row => getColumnValue(row, config, t)))}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      ref={componentRef}
    />
  );
});

const Enum = React.forwardRef(function Enum({ config }: Props, ref: any) {
  const { componentRef, onChange, value, t } = useFilter(config, ref);
  const placeholder = usePlaceholder(config);

  return (
    <EnumBase
      value={value || ""}
      onChange={onChange}
      map={typeof config.map === "function" ? config.map(t) : config.map}
      placeholder={placeholder}
      ref={componentRef}
    />
  );
});

const Bool = React.forwardRef(function Bool({ config }: Props, ref: any) {
  const { onChange, value, t } = useFilter(config, ref);

  return (
    <BoolBase
      value={value === undefined ? true : value}
      onChange={onChange}
      label={getLabel(config.label, config.key, t)}
    />
  );
});

const Range = React.forwardRef(function Range({ config }: Props, ref: any) {
  const { onChange, value } = useFilter(config, ref);

  return (
    <RangeBase
      value={value || { min: config.min, max: config.max }}
      onChange={onChange}
      min={config.min}
      max={config.max}
    />
  );
});

const Date = React.forwardRef(function Date({ config }: Props, ref: any) {
  const { onChange, value, componentRef } = useFilter(config, ref);
  const placeholder = usePlaceholder(config);

  return (
    <DateBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      type={config.selectType}
      ref={componentRef}
    />
  );
});

const Tags = React.forwardRef(function Tags({ config }: Props, ref: any) {
  const { onChange, value, componentRef, data, t } = useFilter(config, ref);
  const placeholder = usePlaceholder(config);

  const availableTags = React.useMemo(() => {
    const allTags = data
      .map(row => getColumnValue(row, config, t))
      .reduce((red, currentTags) => [...red, ...currentTags], []);

    return uniqBy((x: any) => x.id, allTags);
  }, [data]);

  return (
    <TagsBase
      value={value || []}
      onChange={onChange}
      placeholder={placeholder}
      items={availableTags}
      ref={componentRef}
    />
  );
});

const map = {
  string: String,
  enum: Enum,
  bool: Bool,
  range: Range,
  date: Date,
  tags: Tags
};

export default map;
